/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, ReplaySubject, Subscriber, Subscription, timer, zip, of, defer } from 'rxjs';
import { tap } from 'rxjs/operators';

type NotObject = Exclude<keyof any, keyof object>;

class ClearableCacheSubject<T> extends ReplaySubject<T> {
    private lastValue: T | null = null;
    private baseNext: (value: T) => void;

    constructor(cacheSize: number) {
        super(cacheSize);
        this.baseNext = this.next;
        this.next = this.customNext;
    }

    getValue(): T {
        return this.lastValue;
    }

    setValue(value: T) {
        this.lastValue = value;
    }

    customNext(value?: T): void {
        this.lastValue = value;
        this.baseNext(value);
    }

    clear(): void {
        this.lastValue = null;
    }
}

export class SingleEventStream<successType> {
    private internalSubject: ReplaySubject<successType> = null;
    private subscription: Subscription = null;

    constructor(
        private workerFn: () => Observable<successType>,
        lazyInit: boolean = true,
        private cacheLastResult: boolean = true,
        private clearCacheOnRefresh: boolean = true,
        private cacheLifeTime = -1
    ) {
        if (!lazyInit) {
            this.get$();
        }
    }

    /**
     * Starts refreshing data upon called and returns data as an
     * observable which was stored as replaySubject
     */
    refresh(): Observable<successType> {
        const thisCallSubject = new ReplaySubject<successType>(1);

        if (this.cacheLastResult && this.internalSubject instanceof ClearableCacheSubject && this.clearCacheOnRefresh) {
            (this.internalSubject as ClearableCacheSubject<successType>).clear();
        }

        const newData = this.ensureEntryIsInitialized(true);
        if (newData) {
            this.pipeNewDataToReplaySubject(newData, thisCallSubject);
        }

        return thisCallSubject.asObservable();
    }

    setData(value: successType, propagate = false) {
        if (this.internalSubject instanceof ClearableCacheSubject) {
            this.internalSubject.setValue(value);
        }
        if (propagate) {
            this.ensureEntryIsInitialized(false);
            this.pipeNewDataToReplaySubject(of(value), null);
        }
    }

    getData(): successType {
        if (this.internalSubject instanceof ClearableCacheSubject) {
            return this.internalSubject.getValue();
        }
        return null;
    }

    /**
     * Starts fetching new data on called if data does not exist.
     * Returns data as an observable which was stored as replaySubject
     */
    get$(): Observable<successType> {
        const newData = this.ensureEntryIsInitialized(false);
        if (newData) {
            this.pipeNewDataToReplaySubject(newData, null);
        }

        return this.internalSubject.asObservable();
    }

    /**
     * Starts refreshing data upon subscribed and returns data as an
     * observable
     */
    getRefreshObservable(): Observable<successType> {
        return defer(() => this.refresh());
    }

    private pipeNewDataToReplaySubject(
        observable: Observable<successType>,
        outputSubject: ReplaySubject<successType> | null
    ) {
        // This work must be done on next tick, since it can possible invalidate the replay subject
        // which is returned. If we don't delay this a synchronous error returned from this.workerFn()
        // can cause get$() to return null.
        timer(0)
            .pipe(
                tap(
                    val =>
                        (this.subscription = observable.subscribe(
                            x => {
                                if (outputSubject) {
                                    outputSubject.next(x);
                                }

                                if (this.internalSubject) {
                                    this.internalSubject.next(x);
                                }

                                if (this.cacheLastResult && this.cacheLifeTime >= 0) {
                                    timer(this.cacheLifeTime).subscribe(() => {
                                        this.internalSubject = null;
                                    });
                                }
                            },
                            e => {
                                const oldInternalSubject = this.internalSubject;
                                if (this.subscription) {
                                    this.subscription.unsubscribe();
                                }
                                this.internalSubject = null;

                                if (outputSubject) {
                                    outputSubject.error(e);
                                }

                                if (oldInternalSubject) {
                                    oldInternalSubject.error(e);
                                }
                            }
                        ))
                )
            )
            .subscribe();
    }

    private ensureEntryIsInitialized(forceRefresh: boolean): Observable<successType> | null {
        // Check if we've already subscribed for the data, if not add one
        // and add a new ReplaySubject for it.
        if (forceRefresh || !this.internalSubject) {
            // Unsubscribe previous subscription if it exists
            if (this.subscription) {
                this.subscription.unsubscribe();
            }

            // Create a subject if needed, otherwise reuse
            if (!this.internalSubject) {
                this.internalSubject = this.createNewSubject();
            }

            const newData$ = this.workerFn();
            return newData$;
        }
        return undefined;
    }

    private createNewSubject() {
        return this.cacheLastResult ? new ClearableCacheSubject<successType>(1) : new ReplaySubject<successType>(1);
    }
}

export class MultipleEventStream<keyType extends NotObject, successType> {
    private eventStreamMap: Map<keyType, SingleEventStream<successType>> = new Map<
        keyType,
        SingleEventStream<successType>
    >();

    constructor(
        private workerFn: (key: keyType) => Observable<successType>,
        private lazyInit: boolean = true,
        private cacheLastResult: boolean = true,
        private clearCacheOnRefresh: boolean = true,
        private cacheLifeTime = -1
    ) {}

    refresh(key: keyType): Observable<successType> {
        this.ensureMapEntryExists(key);
        return this.eventStreamMap.get(key).refresh();
    }

    refreshAll(): Observable<successType[]> {
        const observables: Observable<successType>[] = [];
        this.eventStreamMap.forEach((value: SingleEventStream<successType>, key: keyType) => {
            observables.push(value.refresh());
        });
        return zip(...observables);
    }

    setData(key: keyType, value: successType, propagate = false) {
        this.ensureMapEntryExists(key);
        this.eventStreamMap.get(key).setData(value, propagate);
    }

    getData(key: keyType): successType {
        this.ensureMapEntryExists(key);
        return this.eventStreamMap.get(key).getData();
    }

    get$(key: keyType): Observable<successType> {
        this.ensureMapEntryExists(key);
        return this.eventStreamMap.get(key).get$();
    }

    private ensureMapEntryExists(key: keyType) {
        if (!this.eventStreamMap.has(key)) {
            this.eventStreamMap.set(
                key,
                new SingleEventStream<successType>(
                    () => this.workerFn(key),
                    this.lazyInit,
                    this.cacheLastResult,
                    this.clearCacheOnRefresh,
                    this.cacheLifeTime
                )
            );
        }
    }
}
